import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { SessionUser } from "@/services/auth";

export type UserContextValue = {
  user: SessionUser | null;
  setUser: (user: SessionUser | null) => void;
};

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({
  children,
  initialUser = null,
}: {
  children: ReactNode;
  initialUser?: SessionUser | null;
}) {
  const [user, setUser] = useState<SessionUser | null>(initialUser);
  const value = useMemo(() => ({ user, setUser }), [user]);
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUserContext(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUserContext must be used within UserProvider");
  }
  return ctx;
}

export function useOptionalUser(): SessionUser | null {
  return useContext(UserContext)?.user ?? null;
}

/** Stable setter helper for tests / composition. */
export function useSetUser() {
  const { setUser } = useUserContext();
  return useCallback((user: SessionUser | null) => setUser(user), [setUser]);
}
